import { captureOnce, capturePaletteOpened, onSurfaceOpened } from "@reflecto/client-core";
import { type AIAvailability, ChromeBuiltinAIProvider } from "@reflecto/client-core/ai";
import {
  BottomNavBar,
  ClipboardScreen,
  type ClipboardScreenLabels,
  CommandPalette,
  type CommandPaletteLabels,
  type ConversationNotificationItemLabels,
  type DockClip,
  FooterHint,
  type FooterHintLabels,
  HeaderBar,
  type HeaderBarLabels,
  IconButton,
  type NavTab,
  type NotificationListItemLabels,
  type PaletteCommand,
  PrivacyModeList,
  type PrivacyModeListLabels,
  PublicModeToggle,
  SidePanelNotificationList,
  type SidePanelNotificationListLabels,
  Text,
  useAIStatus,
  useSeenBatch,
  useTabNavigation,
} from "@reflecto/client-ui";
import { Clipboard, File02, Home01, Stars01 } from "@untitledui/icons";
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { createWebClipboardCapabilities } from "../adapters/clipboard.web";
import { webStorage } from "../adapters/storage.web";
import { FileDestination } from "../components/FileDestination";
import { Glance } from "../components/Glance";
import { InstallPrompt } from "../components/InstallPrompt";
import { PairConfirmCardSlot } from "../components/PairConfirmCardSlot";
import { UpdatePrompt } from "../components/UpdatePrompt";
import { useFileTransfers } from "../filetransfer/useFileTransfers";
import { useExpiredNotificationPrune } from "../hooks/useExpiredNotificationPrune";
import { useStorage } from "../hooks/useStorage";
import { messages, relativeTimeLabels } from "../i18n";
import { installCommandPaletteListener, installLeaderChordListener } from "../lib/keyboard";
import { logWarn } from "../lib/log";
import { navigate, useRoute } from "../lib/router";
import {
  getAnalytics,
  trackClipboardManagerOpened,
  trackClipComposerSent,
  trackClipCopiedFromHistory,
  trackGlanceViewed,
  trackSeenBatch,
  withCommandAnalytics,
} from "../services/analytics";
import {
  type ClipHistoryEntry,
  clearClips,
  getClips,
  hydrateClips,
  removeClip,
  subscribeClips,
} from "../services/clipsStore";
import { conversations as conversationsStore } from "../services/conversationsStore";
import { markAllRead, markNotificationsRead } from "../services/markRead";
import {
  dismissAllNotifications,
  dismissConversation,
  dismissNotification,
  dispatchAction,
  sendReply,
} from "../services/messages";
import { getPublicMode, subscribePublicMode, togglePublicMode } from "../services/publicModeStore";
import { refreshPushCapability } from "../services/pushCapability";
import styles from "./MainPage.module.css";
import { buildPaletteCommands } from "./paletteCommands";

/**
 * Web's primary notification surface.
 *
 * Hosts the headless `SidePanelNotificationList` from
 * `@reflecto/client-ui` — same grouping / search / timeline /
 * IntersectionObserver-auto-mark-read state machine the extension
 * uses, with platform-specific bindings injected here:
 *
 *   - data: `useStorage` reads of notifications/conversations/
 *     appIconCache/groupByApp/unreadCount; `Object.values()` flattens
 *     the conversations map (web stores it as a Record keyed by
 *     thread key, the headless component takes a flat array).
 *   - mark-read: inline storage mutators in `services/markRead`
 *     (no background worker — single React tree).
 *   - dismiss/reply/action: the same `services/messages` helpers the
 *     prior flat-list MainPage already used.
 *   - mark-conversation-read: shared `conversationsStore` factory
 *     (so the in-memory msgId index stays consistent across the SSE
 *     write path and this UI write path).
 *   - header slot: the shared `HeaderBar` with the live pairing-status
 *     dot (`paired` / `sseConnected` are passed through below). Web has
 *     no theme toggle yet.
 *
 * The SSE pipeline is owned by `App.tsx` (one level up) so navigating
 * to SettingsPage doesn't tear down the connection. Label bags below
 * are sourced from the `messages` catalog in `apps/web/src/i18n/`.
 */

// Label bags re-shape the catalog into the prop shapes each headless
// component expects. The headless contracts in `@reflecto/client-ui`
// dictate the keys (`replyAriaLabel`, `noMatchesDesc`, etc.); the
// catalog supplies the strings. Banner name matches the extension's
// `t("extActionTitle")` ("Reflecto") so screen-reader landmark
// navigation lands on the same identity across both surfaces.
/**
 * Bottom-nav destinations for the web surface.
 *
 * Deliberately one short of the extension's union: there is no SMS code in
 * apps/web at all, so the destination cannot exist here. Keeping the two
 * unions separate rather than sharing one is the point — each host declares
 * the routes it can actually serve, and `BottomNavBar` is generic over the
 * union so `onSelect` hands back a narrowed id with no cast.
 */
type NavTabId = "home" | "clipboard" | "file";

const NOTIFICATION_LABELS: NotificationListItemLabels = {
  relativeTime: relativeTimeLabels,
  replyAriaLabel: messages.notifications.replyAriaLabel,
  replyPlaceholder: messages.notifications.replyPlaceholder,
  sendLabel: messages.notifications.replySend,
  sent: messages.notifications.sent,
  failedToSend: messages.notifications.failedToSend,
  unread: messages.notifications.unread,
  dismiss: messages.notifications.dismiss,
  notificationImageAlt: messages.notifications.notificationImageAlt,
};

const CONVERSATION_LABELS: ConversationNotificationItemLabels = {
  relativeTime: relativeTimeLabels,
  replyAriaLabel: messages.conversations.replyAriaLabel,
  replyPlaceholder: messages.conversations.replyPlaceholder,
  sendLabel: messages.conversations.replySend,
  sent: messages.conversations.sent,
  failedToSend: messages.conversations.failedToSend,
  // unreadAriaLabel is a function so its plural rule lives in the catalog —
  // a future locale can override it without touching this file.
  unreadAriaLabel: messages.conversations.unreadAriaLabel,
  dismiss: messages.conversations.dismiss,
  messagesListLabel: messages.conversations.messagesListLabel,
  deviceOwner: messages.conversations.deviceOwner,
};

const WEB_HEADER_LABELS: HeaderBarLabels = {
  banner: messages.header.banner,
  statusConnected: messages.header.statusConnected,
  statusReconnecting: messages.header.statusReconnecting,
  statusDisconnected: messages.header.statusDisconnected,
  openSettings: messages.header.openSettings,
};

const LIST_LABELS: SidePanelNotificationListLabels = {
  today: messages.list.today,
  yesterday: messages.list.yesterday,
  searchNotifications: messages.list.searchNotifications,
  clearSearch: messages.list.clearSearch,
  clearAll: messages.list.clearAll,
  clearMatches: messages.list.clearMatches,
  markAllRead: messages.list.markAllRead,
  groupCheckboxLabel: messages.list.groupCheckboxLabel,
  noNotificationsTitle: messages.list.noNotificationsTitle,
  noNotificationsDesc: messages.list.noNotificationsDesc,
  noMatchesTitle: messages.list.noMatchesTitle,
  noMatchesDesc: messages.list.noMatchesDesc,
  notificationsList: messages.list.notificationsList,
  appNotificationsGroup: messages.list.appNotificationsGroup,
  dismissAllFrom: messages.list.dismissAllFrom,
  clear: messages.list.clear,
  // keyboardHint intentionally omitted here — the keycap footer is rendered as
  // the shell's bottom-most row (below the clipboard destination), not inside
  // SidePanelNotificationList. Keyboard handlers stay active via `enableKeyboard`.
};

// Privacy mode is a separate component with its own (much smaller) bag. The
// empty-state strings are shared with the list on purpose — "no notifications"
// reads the same whether or not content is masked.
const PRIVACY_LABELS: PrivacyModeListLabels = {
  privacyAppList: messages.list.privacyAppList,
  privacyCount: messages.list.privacyCount,
  noNotificationsTitle: messages.list.noNotificationsTitle,
  noNotificationsDesc: messages.list.noNotificationsDesc,
};

// Keyboard keycap hint, rendered below the dock so the dock is the primary
// bottom chrome and the passive hint is the true footer.
const KEYBOARD_HINT: FooterHintLabels = {
  dismiss: messages.list.kbdDismiss,
  navigate: messages.list.kbdNavigate,
  orSeparator: messages.list.kbdOrSeparator,
  // activatePrompt omitted: web renders this footer without a `focused` prop
  // (always keycaps — a normal tab owns focus), so the unfocused prompt never shows.
};

// Stable, immutable server snapshot for `useSyncExternalStore` — clip history
// is IndexedDB-backed with no SSR, so the server snapshot is always empty.
// A module-level constant keeps the reference identity stable per React's
// contract. (The infinite-re-render risk lives on the CLIENT snapshot — the 2nd
// `useSyncExternalStore` arg — where returning a fresh `[]` each call would loop;
// the server snapshot, this 3rd arg, is never called in the SPA, so `EMPTY_CLIPS`
// is purely about a stable immutable identity if SSR is ever added.)
const EMPTY_CLIPS: readonly ClipHistoryEntry[] = [];

// Clipboard dock label bag — re-shapes `messages.clipboard.*` into the
// headless `ClipboardScreen` contract. Several existing clipboard/card catalog keys
// are mapped onto the dock's differently-named props — e.g.
// sendNoDevices→sentNone, sendSuccess→sentToast, sensitiveChip→card.sensitive,
// cloudChip→card.cloud, sendPlaceholder→composerPlaceholder,
// sendCta→composerSend, sendSensitiveToggle→markSensitive, empty→emptyBody —
// alongside the directly-named reuses where the prop name matches the catalog
// key as-is (from/copy/copied/reveal/hide/maskedHint/delete/clearAll/
// clearAllConfirm/clearAllCancel/emptyTitle/pasteFromClipboard) plus the
// surviving `dock*` keys for the RECENT chrome.
//
// `card.to`/`card.delete`/`clearAll`/`clearAllConfirm`/`clearAllCancel` are
// required, real-copy keys in every one of the 28 web catalogs (web has no
// l10n script backstop — `satisfies MessagesShape` IS the parity check, so
// these ship all-locale rather than en+es-only). `ClipCard`/`ClipboardScreen`
// still gate each control on BOTH the callback and its label being present
// (a `client-ui` contract shared with the not-yet-wired extension host), but
// every web locale now supplies a real translation.
const CLIPBOARD_LABELS: ClipboardScreenLabels = {
  sectionLabel: messages.clipboard.title,
  // C6: the send-confirm popover is gone. FOUR of its five strings
  // (dockSendTitle/dockFromClipboard/dockSend/dockCancel) are retired from
  // every catalog. The fifth — `sendSensitiveToggle`, already "Mark sensitive"
  // in all 28 — IS the composer lock toggle's name verbatim, so it is reused
  // under the new prop rather than retired-and-re-added (same move the R7
  // empty state made with `empty`→`emptyBody`). That keeps 28 existing
  // translations and means this task adds NO new locale key at all.
  markSensitive: messages.clipboard.sendSensitiveToggle,
  // C9: reuses the notification list's OWN date-separator strings rather than
  // adding clipboard-scoped duplicates. This spec exists because the two
  // screens read as different products; two independently-translated words
  // for "Today" is exactly how that drift restarts. Same keys the mirrored
  // list passes at `LIST_LABELS` above.
  today: messages.list.today,
  yesterday: messages.list.yesterday,
  card: {
    relativeTime: relativeTimeLabels,
    from: messages.clipboard.from,
    to: messages.clipboard.to,
    copy: messages.clipboard.copy,
    copied: messages.clipboard.copied,
    reveal: messages.clipboard.reveal,
    hide: messages.clipboard.hide,
    sensitive: messages.clipboard.sensitiveChip,
    cloud: messages.clipboard.cloudChip,
    maskedHint: messages.clipboard.maskedHint,
    delete: messages.clipboard.delete,
  },
  clearAll: messages.clipboard.clearAll,
  clearAllConfirm: messages.clipboard.clearAllConfirm,
  clearAllCancel: messages.clipboard.clearAllCancel,
  pasteFromClipboard: messages.clipboard.pasteFromClipboard,
  recentHeading: messages.clipboard.dockRecentHeading,
  emptyTitle: messages.clipboard.emptyTitle,
  // The R7 empty state splits the old one-line `empty` into a headline plus a
  // body. The existing `clipboard.empty` string IS that body verbatim, so it
  // is reused under the new name rather than retired-and-re-added — that keeps
  // all 28 already-translated values and leaves `emptyTitle` as the only new
  // sentence needing translation.
  emptyBody: messages.clipboard.empty,
  emptyClipboardTip: messages.clipboard.dockEmptyClipboard,
  readFailedTip: messages.clipboard.readFailed,
  // MVP: 1 partner only, so we explicitly drop the `total` arg. Under
  // multi-device this would mis-render a partial send (e.g. "1 of 3"); revisit then.
  sentToast: (sent) => messages.clipboard.sendSuccess(sent),
  sentNone: messages.clipboard.sendNoDevices,
  composerPlaceholder: messages.clipboard.sendPlaceholder,
  composerSend: messages.clipboard.sendCta,
};

// i18n label bag for the headless CommandPalette's chrome (placeholder / no-results /
// aria-label). Per-command labels are state-dependent (e.g. the privacy toggle's
// on/off text) and are picked inside `buildPaletteCommands` at assembly time instead.
const COMMAND_PALETTE_LABELS: CommandPaletteLabels = {
  placeholder: messages.palette.placeholder,
  noResults: messages.palette.noResults,
  ariaLabel: messages.palette.ariaLabel,
};

// Stable empty reference for when the palette is closed — see the
// `paletteCommands` memo below for why the build is skipped entirely then.
const NO_PALETTE_COMMANDS: PaletteCommand[] = [];

// Module-level for stable identity — useAIStatus puts these in effect deps. Shared between the
// header AI-recap button (capability gate) and the `Glance` card (both read one probe).
const aiStatusBindings = {
  loadInitial: async () => {
    const s = await webStorage.get("aiLastAvailability");
    return { lastAvailability: s.aiLastAvailability ?? undefined };
  },
  persistLastAvailability: (next: AIAvailability) => webStorage.set({ aiLastAvailability: next }),
};

async function handleMarkConversationRead(key: string): Promise<void> {
  try {
    await conversationsStore.markConversationRead(key);
  } catch (err) {
    // Headless calls this fire-and-forget (`void onMarkConversationRead(...)`)
    // per the prop's "caller-logged" contract — surface the failure here
    // since this is the host's storage-coupled DI seam.
    //
    // The conversation `key` (`packageName|id|tag`) is intentionally
    // omitted: it carries the Android app's package name, which the
    // architecture forbids the server from seeing
    // (apps/server/CLAUDE.md). Today this is a browser console.warn
    // — low risk — but when remote error reporting (Sentry/OTLP)
    // wires in front of `logWarn`, the metadata would leak to a
    // third-party sink. Cheaper to omit at source than redact later.
    logWarn("messages", "mark_conversation_read_failed", err);
  }
}

export function MainPage() {
  const { value: androidDeviceName } = useStorage("androidDeviceName");
  const { value: notifications } = useStorage("notifications");

  // Active User anchor (§4.2): debounced open event + drain of the SW's bookkeeping.
  useEffect(() => {
    void onSurfaceOpened(webStorage, getAnalytics(), "web_app_opened", "app_opens");
    void captureOnce(webStorage, getAnalytics(), "web_app_first_open");
    // Re-check whether the phone can be woken by push — it decides whether reply and action
    // controls are offered at all. Fire-and-forget: the read above is reactive, so a late
    // write still lands, and nothing here should delay first paint.
    void refreshPushCapability();
  }, []);

  // Seen = rendered while visible (§3.3); session-scoped dedup, First Light split, and
  // the visibility gate all live in the shared hook (slice 4, R4-d-2) -- this block was
  // byte-identical to apps/web's copy, and a third surface is about to need it.
  useSeenBatch(notifications, trackSeenBatch);
  const { value: conversations } = useStorage("conversations");
  const { value: appIconCache } = useStorage("appIconCache");
  const { value: groupByApp } = useStorage("groupByApp");
  const isGrouped = groupByApp === true;

  const handleToggleGrouped = useCallback((checked: boolean) => {
    void webStorage.set({ groupByApp: checked });
  }, []);
  const { value: unreadCount } = useStorage("unreadCount");
  const { value: paired } = useStorage("paired");
  const { value: sseConnected } = useStorage("sseConnected");

  // Absent means "not asked yet" (first load, or the refresh below hasn't landed) and is
  // treated as capable on purpose — an unknown state must never silently remove working
  // controls. Only an explicit false from the server withholds them.
  const { value: phonePushCapable } = useStorage("phonePushCapable");
  const interactionsUnavailable = phonePushCapable === false;

  // TTL auto-dismiss for server-origin (`/v1/send`) notifications: prune
  // `expiresAt`-elapsed cards from storage on mount + on an interval. No-op for
  // device-mirrored notifications (they carry no `expiresAt`).
  useExpiredNotificationPrune(paired === true);

  // The pairing flow sets `pairingJustConfirmed: true` so the confirm
  // screen can animate. Flip it back to false shortly after landing
  // on MainPage \u2014 the extension does the same via setTimeout in
  // completePairing; the web app does it from the UI side to avoid
  // a stray timer surviving across tab closes.
  useEffect(() => {
    const t = setTimeout(() => {
      void webStorage.set({ pairingJustConfirmed: false });
    }, 3000);
    return () => clearTimeout(t);
  }, []);

  // Web stores conversations as a Record<key, ConversationThread> so
  // single-thread lookups + writes stay O(1). The headless component
  // takes a flat array and does its own grouping, so flatten here.
  // Memoised on the underlying record so unrelated parent re-renders
  // don't churn the headless component's effect deps.
  const conversationsList = useMemo(() => Object.values(conversations ?? {}), [conversations]);

  // Stable ref so HeaderBar doesn't see a fresh function identity on
  // every MainPage re-render — useStorage flips on every IDB write,
  // so MainPage re-renders frequently and an inline `() => navigate(
  // ...)` would defeat any downstream memoization keyed on the
  // callback prop. (HeaderBar today doesn't list `onOpenSettings` in
  // a useEffect dep, so this is preventive — but matches the
  // `onMainViewActive` stability rule the headless AppShell
  // documents.)
  const handleOpenSettings = useCallback(() => navigate({ name: "settings" }), []);

  // Public mode is in-memory only — V1 spec demands the flag reset to
  // OFF on every reload so the fail-safe is "showing notifications".
  // We deliberately don't persist this through `webStorage`. See the
  // matching note on the extension's SidePanelNotificationList wrapper
  // and the JSDoc on `ReflectoStorage.publicMode`.
  //
  // Module-scope store (not local `useState`): SettingsPage's safety-number
  // masking needs to read the SAME flag, and `App.tsx` mounts MainPage and
  // SettingsPage as mutually-exclusive routes (never both at once), so a
  // component-local state here would be unreachable from Settings. See
  // `services/publicModeStore.ts`. `togglePublicMode` is already a stable
  // top-level function reference — no `useCallback` needed.
  const publicMode = useSyncExternalStore(subscribePublicMode, getPublicMode, () => false);

  // Persisted clip history (spec C1) — sent + received, IndexedDB-backed via
  // `createClipHistory`. `hydrate()` now notifies subscribers itself once it
  // actually populates the mirror (client-core fix), so the mount effect
  // below just kicks off the one-time load — `subscribeClips`'s
  // `useSyncExternalStore` wiring below picks up the populated mirror on its
  // own; no separate re-render bump is needed.
  useEffect(() => {
    void hydrateClips();
  }, []);
  const storedClips = useSyncExternalStore<readonly ClipHistoryEntry[]>(
    subscribeClips,
    getClips,
    () => EMPTY_CLIPS
  );
  // Map each history entry to the headless `DockClip` shape; the screen
  // renders the latest in its collapsed bar and the full list when expanded.
  // `provenance` is carried from the stored entry (client-core's
  // `ClipHistoryEntry.provenance`, populated by the inbound clipboard branch
  // in `services/notifications.ts` from the wire payload's own `provenance`
  // field). `DockClip.provenance` is a required "device" | "cloud" union, so
  // default to "device" for any pre-existing entry that predates the field —
  // every write path today (`services/clipboard.ts`'s `broadcastClip` for
  // sent clips, the inbound branch for received ones) always populates a
  // real value, so this fallback only guards stale persisted history.
  const dockClips: DockClip[] = useMemo(
    () =>
      storedClips.map((c) => ({
        id: c.id,
        content: c.content,
        originLabel: c.peerLabel,
        timestamp: c.timestamp,
        sensitive: c.sensitive,
        provenance: c.provenance ?? "device",
        direction: c.direction,
      })),
    [storedClips]
  );
  // Outbound capabilities (clipboard read/write + broadcast fan-out). Stable
  // identity so the dock's callbacks don't churn on every MainPage re-render.
  const clipCaps = useMemo(() => createWebClipboardCapabilities(), []);

  // Per-item delete + clear-all (C5) — stable identities, mirroring
  // `handleToggleGrouped`'s rationale above. Fire-and-forget: the store's
  // sync mirror + `notify()` already updates the list; a failed persist is
  // swallowed inside `createClipHistory` (never rejects, never logs content).
  const handleDeleteClip = useCallback((id: string) => {
    void removeClip(id);
  }, []);
  const handleClearAllClips = useCallback(() => {
    void clearClips();
  }, []);

  // Analytics-only hooks (spec §4.4) — page context, never the SW (no SDK
  // there). `onClipCopied` receives the full clip because `direction` is the
  // one property the event needs; `onComposerSend` fires only once
  // `ClipboardScreen`'s internal broadcast() actually reached a device. The
  // composer is the sole send path since the redesign (R2), so that one
  // callback covers every send here — `onQuickSend`/`clip_quick_sent` are
  // retired (see `docs/analytics/event-spec.md`).
  const handleClipCopied = useCallback((clip: DockClip) => {
    trackClipCopiedFromHistory(clip.direction ?? "received");
  }, []);
  const handleComposerSend = useCallback(() => {
    trackClipComposerSent();
  }, []);

  // One AI probe drives both the header recap button's capability gate and the `Glance` card.
  // The card needs the full status (download / progress / errorMessage); the button needs only the
  // capability boolean — so own `useAIStatus` here and inject the status down to the card.
  const provider = useMemo(() => new ChromeBuiltinAIProvider(), []);
  const aiStatus = useAIStatus({ provider, ...aiStatusBindings });
  // AI-capable = the model is usable or installable: allowlist the usable availability states.
  // (A blocklist like `!== "unsupported"` lets "unavailable" — policy-blocked / model-removed —
  // through, rendering a button whose every trigger errors with no recovery path.) Also gate on the
  // live requirements report so a stale storage-restored "available" can't show the button before
  // the probe confirms (`null` = pre-probe).
  const aiCapable =
    (aiStatus.availability === "downloadable" ||
      aiStatus.availability === "downloading" ||
      aiStatus.availability === "available") &&
    (aiStatus.requirements?.ok ?? false);

  // On-demand AI recap: the header "Glance" button bumps this counter, which the headless
  // `Glance` watches as `trigger` to surface the card + run a last-24h recap. In-memory only —
  // a recap is a user-initiated action, nothing to persist.
  const [recapTrigger, setRecapTrigger] = useState(0);
  // Jump signal: the recap card calls `onJumpTo` with a target; bumping `signal` reveals + scrolls
  // + highlights that row in the notification list.
  // Destinations, mirroring the extension's bottom nav. Gating happens by
  // omitting an entry, never by passing one and hiding it — BottomNavBar
  // sizes tabs by how many it receives. Web has no SMS surface, so this tops
  // out at three where the panel reaches four.
  const tabs = useMemo<NavTab<NavTabId>[]>(() => {
    const list: NavTab<NavTabId>[] = [
      { id: "home", label: messages.nav.home, icon: Home01 },
      { id: "clipboard", label: messages.nav.clipboard, icon: Clipboard },
    ];
    list.push({ id: "file", label: messages.nav.files, icon: File02 });
    return list;
  }, []);

  // Selection + the disappearing-destination fallback come from the shared
  // hook, so that rule has one definition across both surfaces. In-memory by
  // design, same as the panel: a destination has no meaning across sessions,
  // so a reload lands on Home.
  const { activeId: activeTab, selectTab } = useTabNavigation(tabs, "home");

  // Route Home before bumping, same rule as the deep-link and summon paths:
  // the recap card renders into the notification list's `recap` slot, which
  // exists only on Home. Triggering Glance from Clipboard/File would
  // otherwise look like nothing happened.
  const handleGlance = useCallback(() => {
    selectTab("home");
    setRecapTrigger((n) => n + 1);
    trackGlanceViewed();
  }, [selectTab]);

  const [jump, setJump] = useState<
    { target: { msgId: string } | { conversationKey: string }; signal: number } | undefined
  >(undefined);

  // SW deep-link: bump the shared `jump` signal; one-shot per key, waits out cold-start.
  const route = useRoute();
  const deepLinkedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (route.name !== "conversation") {
      deepLinkedKeyRef.current = null;
      return;
    }
    const key = route.conversationKey;
    if (deepLinkedKeyRef.current === key) return;
    if (!conversationsList.some((c) => c.key === key)) return;
    deepLinkedKeyRef.current = key;
    // Route Home before bumping. Only the notification list consumes `jump`,
    // and it renders on Home alone — so a deep-link arriving while the user
    // is parked on Clipboard/File would otherwise scroll nothing. Worse than
    // a no-op: the ref above has already marked this key handled, so it would
    // never replay even after the user navigated back.
    selectTab("home");
    setJump((j) => ({ target: { conversationKey: key }, signal: (j?.signal ?? 0) + 1 }));
  }, [route, conversationsList, selectTab]);

  // Leader chord (PRD docs/prd-keyboard.md §5.8). macOS/Linux use
  // Ctrl+Shift+K, Windows uses Alt+Shift+K, macOS additionally accepts
  // Cmd+Shift+K (the manifest's `mac` slot in the extension uses
  // Command — the web honours both so muscle memory carries across
  // surfaces). Bumping focusSignal cascades into the headless list
  // which re-selects the first unread row.
  const [focusSignal, setFocusSignal] = useState(0);
  useEffect(() => {
    // Prefer User-Agent Client Hints (Chromium); `navigator.platform` is the deprecated fallback.
    const uaData = (navigator as { userAgentData?: { platform?: string } }).userAgentData?.platform;
    const legacy = typeof navigator.platform === "string" ? navigator.platform : "";
    return installLeaderChordListener({
      // `||` rather than `??` so an empty `uaData` falls back too.
      // `navigator.userAgentData.platform` returns a non-empty
      // string on every supporting Chromium today, but defensively
      // an empty string would otherwise pass through `??` and
      // mis-route the chord to the Linux branch on any OS.
      platform: uaData || legacy,
      // PRD §5.2: the leader is a modifier chord; the input-trap
      // guard (`shouldFireShortcut`) is for single-letter shortcuts
      // and would silently no-op summon while focus is in the
      // search box. Only block IME composition here — that's the
      // one state where a chord could collide with text input.
      shouldFire: (e) => !e.isComposing && e.code !== "Process",
      // Same reason as the deep-link above: `focusSignal` is consumed only
      // by the notification list, which renders on Home. Without this the
      // summon chord is a silent no-op on every other destination. Mirrors
      // `summonToTopUnread` in the extension's SidePanel.
      onSummon: () => {
        selectTab("home");
        setFocusSignal((n) => n + 1);
      },
    });
  }, [selectTab]);

  // ⌘K / Ctrl+K command palette (docs/superpowers/specs/2026-07-12-extension-command-palette-design.md
  // §11). Plain document-scoped keydown, registered beside (not instead of) the leader-chord
  // listener above — the two matchers are mutually exclusive on the shiftKey axis
  // (`isCommandPaletteChordEvent` rejects shiftKey, `isLeaderChordEvent` requires it), so
  // Shift+⌘K keeps summoning the column while plain ⌘K opens the palette. A second ⌘K while
  // already open toggles it closed — an internal UX choice, not spec'd, mirroring the extension.
  // File transfer (TASK-27 M4) — send/receive UI state + the app-open
  // resume triggers (§5.1/§6.1). Arg-gated on `paired` so an unpaired app does
  // zero file-transfer work (no IndexedDB resume scans, no OPFS GC, no
  // file_ctrl handler registration). React hook rules forbid gating the CALL
  // itself, hence the argument gate.
  const fileTransfers = useFileTransfers(paired);

  const [paletteOpen, setPaletteOpen] = useState(false);
  useEffect(() => {
    return installCommandPaletteListener({
      // Same IME-composition guard as the leader chord above — the one state where a modifier
      // chord could collide with text input.
      shouldFire: (e) => !e.isComposing && e.code !== "Process",
      onToggle: () => setPaletteOpen((open) => !open),
    });
  }, []);

  // Palette opened = recurring intent (event-spec §4.2), debounced ≥30 min —
  // independently of the web_app_opened anchor (see capturePaletteOpened's
  // doc comment: the palette only opens from inside an already-open page,
  // so sharing that anchor would suppress it almost every time).
  useEffect(() => {
    if (paletteOpen) {
      void capturePaletteOpened(webStorage, getAnalytics());
    }
  }, [paletteOpen]);

  // Clipboard destination opened = recurring intent (spec §4.4), fired every
  // time the tab becomes active — mirrors Android's `onScreenOpened()`
  // (undebounced; visiting the tab a few times a day is the expected use,
  // unlike the palette which can be toggled rapidly).
  useEffect(() => {
    if (activeTab === "clipboard") {
      trackClipboardManagerOpened();
    }
  }, [activeTab]);

  const { value: dndSnoozeUntil } = useStorage("dndSnoozeUntil");

  // Assembled fresh from live state whenever the palette is open — satisfies spec §5.3
  // ("state-dependent commands/labels computed at open time"): by the time `paletteOpen`
  // flips true, this render's deps already reflect current publicMode/aiCapable/snooze/clips.
  // Skipped (stable empty array) while closed, since notifications/conversations/clips churn
  // on effectively every incoming event and the palette isn't mounted to consume the result.
  const paletteCommands = useMemo(
    () =>
      paletteOpen
        ? buildPaletteCommands({
            notifications: notifications ?? [],
            conversations: conversationsList,
            unreadCount: unreadCount ?? 0,
            publicMode,
            onTogglePublicMode: togglePublicMode,
            onNotificationsTab: activeTab === "home",
            aiCapable,
            onGlance: handleGlance,
            dndSnoozeUntil,
            dockClips,
            clipCapabilities: clipCaps,
            onOpenSettings: handleOpenSettings,
          })
        : NO_PALETTE_COMMANDS,
    [
      paletteOpen,
      notifications,
      conversationsList,
      unreadCount,
      publicMode,
      // `togglePublicMode` is a stable module-level export from
      // `publicModeStore` now (not component-scope state), so it's an
      // "outer scope value" biome's exhaustive-deps lint rejects here.
      aiCapable,
      handleGlance,
      dndSnoozeUntil,
      dockClips,
      clipCaps,
      handleOpenSettings,
      // Rebuild when the destination changes: the privacy command is scoped
      // to the notifications destination, so the command set differs per tab.
      activeTab,
    ]
  );

  // Single seam: instruments all 8 v1 commands at once instead of touching
  // each `run` in paletteCommands.ts (which stays pure/analytics-free).
  const instrumentedPaletteCommands = useMemo(
    () => withCommandAnalytics(getAnalytics(), paletteCommands),
    [paletteCommands]
  );

  // Render the headless list directly — its `.container` is
  // `display: flex; flex-direction: column; height: 100%` and it
  // owns its own search/actions/list padding via the module CSS.
  // An outer padded Box wrapper would both add unwanted insets and
  // break the `height: 100%` (height-100% measures the padded
  // ancestor's content-box). The "Paired with X" chrome lives in
  // the `header` slot below, so the outer Box has nothing else to
  // contribute.
  // Home-contextual banners. Rendered in BOTH the normal and the privacy-mode
  // Home view: the HeaderBar itself is shell chrome and lives above the
  // destination switch, but these are Home's own chrome and are not content, so
  // masking them would be wrong. `InstallPrompt` renders nothing on browsers
  // that don't fire `beforeinstallprompt` — Safari, Firefox, every iOS browser —
  // and nothing once installed, so it's safe to always include.
  const homeBanners = (
    <>
      <UpdatePrompt />
      {/* See main.tsx for why install affordances are gated. */}
      {!import.meta.env.VITE_DISABLE_INSTALL && <InstallPrompt />}
      <PairConfirmCardSlot />
    </>
  );

  return (
    <>
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        commands={instrumentedPaletteCommands}
        labels={COMMAND_PALETTE_LABELS}
      />
      <div className={styles.shell}>
        {/* Shell chrome: visible on every destination, so the settings route and
            the paired/connection status never disappear just because the user
            switched to Clipboard or File. The trailing slot is Home-only —
            both the AI recap trigger and the privacy toggle act on the
            notification list, so they follow it rather than the shell. */}
        <HeaderBar
          deviceLabel={androidDeviceName ?? messages.header.pairedFallbackDevice}
          // Pass `paired` and `sseConnected` THROUGH as `boolean | undefined`
          // — coercing either to `false` would break the headless's
          // loading-window invariant: when EITHER value is undefined the
          // connection live region stays empty so AT doesn't announce a
          // stale "Disconnected" before the storage promise resolves.
          paired={paired}
          sseConnected={sseConnected}
          onOpenSettings={handleOpenSettings}
          trailing={
            activeTab === "home" ? (
              <>
                {aiCapable ? (
                  <IconButton
                    icon={Stars01}
                    label={messages.glance.buttonLabel}
                    onClick={handleGlance}
                  />
                ) : null}
                <PublicModeToggle
                  active={publicMode}
                  onToggle={togglePublicMode}
                  label={
                    publicMode
                      ? messages.header.publicModeToggleDisable
                      : messages.header.publicModeToggleEnable
                  }
                />
              </>
            ) : undefined
          }
          labels={WEB_HEADER_LABELS}
        />
        <div className={styles.main}>
          {activeTab === "home" &&
            (publicMode ? (
              <PrivacyModeList
                notifications={notifications ?? []}
                conversations={conversationsList}
                appIconCache={appIconCache ?? {}}
                // Banners are host chrome, not content — they survive a
                // screen-share, exactly as they did before the split.
                header={homeBanners}
                labels={PRIVACY_LABELS}
              />
            ) : (
              <SidePanelNotificationList
                notifications={notifications ?? []}
                conversations={conversationsList}
                appIconCache={appIconCache ?? {}}
                isGrouped={isGrouped}
                onToggleGrouped={handleToggleGrouped}
                unreadCount={unreadCount ?? 0}
                onMarkRead={(ids) => {
                  void markNotificationsRead(ids);
                }}
                onMarkAllRead={() => {
                  void markAllRead();
                }}
                onDismiss={(msgId) => {
                  void dismissNotification(msgId);
                }}
                onDismissConversation={(key) => {
                  void dismissConversation(key);
                }}
                onDismissAll={({ msgIds, conversationKeys }) => {
                  void dismissAllNotifications(msgIds, conversationKeys);
                }}
                onMarkConversationRead={handleMarkConversationRead}
                onSendReply={sendReply}
                onDispatchAction={dispatchAction}
                interactionsUnavailable={interactionsUnavailable}
                scrollTo={jump}
                recap={
                  <Glance
                    publicMode={publicMode}
                    provider={provider}
                    aiStatus={aiStatus}
                    trigger={recapTrigger}
                    onJumpTo={(target) =>
                      setJump((j) => ({ target, signal: (j?.signal ?? 0) + 1 }))
                    }
                  />
                }
                header={homeBanners}
                enableKeyboard
                focusSignal={focusSignal}
                labels={LIST_LABELS}
                notificationListItemLabels={NOTIFICATION_LABELS}
                conversationNotificationItemLabels={CONVERSATION_LABELS}
              />
            ))}
          {activeTab === "clipboard" && (
            <ClipboardScreen
              clips={dockClips}
              capabilities={clipCaps}
              labels={CLIPBOARD_LABELS}
              publicMode={publicMode}
              onDeleteClip={handleDeleteClip}
              onClearAll={handleClearAllClips}
              onClipCopied={handleClipCopied}
              onComposerSend={handleComposerSend}
            />
          )}
          {/* File transfer — keeps its pre-existing publicMode unmount, so
              the destination is present but empty during screen-share. Same
              known gap as the panel; an explicit
              "hidden in public mode" empty state is a step-5 follow-up. The
              tab must stay open for a transfer to progress (sendKeepOpenHint). */}
          {/* Privacy mode hides the file cards: the picker/device dropdown and
              the offer cards (which show sender filenames) have no sensible
              masked form, so they unmount rather than blur. Say so — an
              unexplained blank destination reads as broken. */}
          {activeTab === "file" && publicMode && (
            <Text size="xs" tone="tertiary">
              {messages.nav.fileHiddenPublicMode}
            </Text>
          )}
          {activeTab === "file" && !publicMode && (
            /* One list for both directions, replacing the two stacked utility
               cards. All state still comes from the single useFileTransfers
               hook; the send machine now lives in `useFileSend` inside the
               destination so both directions can be composed into one list. */
            <FileDestination transfers={fileTransfers} />
          )}
        </div>
        {/* Keyboard keycap hint as the shell's bottom-most row — below the dock.
          Keyboard nav is always on here, so only !publicMode gates it. SC 1.3.1
          evaluated + accepted: the hint reads after the dock controls (not right
          after the list it describes), but the keycaps are supplementary decoration
          — the E/J/K shortcuts work regardless of reading order — so no
          aria-describedby link to the list is added. */}
        {activeTab === "home" && !publicMode && <FooterHint labels={KEYBOARD_HINT} />}
        <BottomNavBar
          tabs={tabs}
          activeId={activeTab}
          onSelect={selectTab}
          ariaLabel={messages.nav.ariaLabel}
        />
      </div>
    </>
  );
}
